<template>
	<div v-if="contractItem" class="flex items-center justify-start mb-4 mt-1">
		<h3 v-if="showTitle" class="mr-auto font-semibold">Item Availability</h3>

		<app-badge
			v-if="contractItemStatus && contractItemStatus === 'Cancelled'"
			class="gap-2 mr-2"
			size="small"
			theme="danger">
			Skipped
		</app-badge>

		<app-badge class="gap-2" size="small" theme="info">
			<b>Item No:</b> {{ contractItem.item_number }}
		</app-badge>

		<app-badge class="gap-2 ml-2" size="small" theme="info">
			<b>Item Description:</b>
			{{ contractItem.description }}
		</app-badge>

		<app-badge class="gap-2 ml-2" size="small" theme="info">
			<b>Quantity Required:</b>
			{{ contractItem.quantity_required }}
		</app-badge>
	</div>

	<datatable v-if="contractItem" :headers :value="items" :loading :paginator="false">
		<template #row-quantity_reserved="{ row }">
			{{ row.quantity_reserved ?? 0 }}
		</template>

		<template #row-status="{ row }">
			<div class="flex grow-0 shrink-1">
				<app-tooltip
					v-if="row.quantity_available > contractItem.quantity_required"
					text="Enough Stock"
					position-x="right">
					<font-awesome-icon
						icon="fa-regular fa-check-circle"
						class="h-5 text-green-400" />
				</app-tooltip>

				<app-tooltip v-else text="Not Enough Stock" position-x="right">
					<font-awesome-icon icon="fa-regular fa-circle-xmark" class="h-5 text-red-400" />
				</app-tooltip>
			</div>
		</template>
	</datatable>
</template>

<script lang="ts" setup>
	import { ref, watch, computed } from 'vue'
	import type { Header } from '@/data/types/datatable'
	import { fetchItemAvailability } from '@/composables/contracts'
	import { ContractItem } from '@/data/schema/contract-item'
	import useAuthStore from '@/stores/auth'

	const props = defineProps({
		contractItem: {
			type: Object as () => ContractItem,
			default: undefined
		},
		showTitle: {
			type: Boolean,
			default: false
		},
		currentDepot: {
			type: Boolean,
			default: false
		}
	})

	const auth = useAuthStore()
	const loading = ref(true)
	const items = ref([])

	const contractItemStatus = computed(() => {
		if (
			!props.contractItem ||
			!props.contractItem.stock_check_status ||
			props.contractItem.stock_check_status.length == 0
		) {
			return null
		}

		return props.contractItem.stock_check_status[0].status ?? null
	})

	const headers: Header[] = [
		{ value: 'depot', text: 'Depot' },
		{ value: 'stock_level', text: 'Stock Lvl' },
		{ value: 'quantity_allocated', text: 'Qty Alloc' },
		{ value: 'quantity_reserved', text: "Qty Res'd" },
		{ value: 'quantity_available', text: 'Available' },
		{ value: 'quantity_service', text: 'Service' },
		{ value: 'quantity_repair', text: 'Repair' },
		{ value: 'status', text: 'Status' }
	]

	const emit = defineEmits(['loading', 'reloaded'])

	/**
	 * Click an item row - get availability
	 * @param item
	 */
	async function getData() {
		emit('loading')
		loading.value = true

		if (props.contractItem) {
			await fetchItemAvailability(
				props.contractItem,
				props.currentDepot ? auth.user!.current_depot : undefined
			).then((res) => {
				items.value = res
				loading.value = false
				emit('reloaded')
			})
		}
	}

	watch(
		() => props.contractItem,
		() => {
			getData()
		}
	)
</script>
